import { useState, useCallback, useEffect } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { toast } from 'sonner'
import { Plus, Save, X } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Spinner } from '@/app/lib/loader/loader'
import { ConfirmationModal } from '../ui/ConfirmationModal'
import {
  navigationService,
  StoreNavigationItem,
} from '../../services/navigation.service'
import { NavigationItemsList } from './NavigationItemsList'
import { NavigationItemModal } from './NavigationItemModal'

interface NavigationItemsManagerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number
  menuName: string
}

export function NavigationItemsManager({
  open,
  onOpenChange,
  menuId,
  menuName,
}: NavigationItemsManagerProps) {
  const queryClient = useQueryClient()

  // ── Estado local de ítems (para orden en tiempo real) ───────────────────
  const [localItems, setLocalItems] = useState<StoreNavigationItem[]>([])
  const [isDirtyOrder, setIsDirtyOrder] = useState(false)
  const [isSavingOrder, setIsSavingOrder] = useState(false)

  // ── Modal de edición de ítem ─────────────────────────────────────────────
  const [itemModalOpen, setItemModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<StoreNavigationItem | null>(null)

  // ── Confirmaciones de estado y eliminación ───────────────────────────────
  const [deleteItem, setDeleteItem] = useState<StoreNavigationItem | null>(null)
  const [statusItem, setStatusItem] = useState<StoreNavigationItem | null>(null)

  // ── Carga de datos del menú ───────────────────────────────────────────────
  const { data: menuData, isLoading } = useQuery({
    queryKey: ['navigation-menu', menuId],
    queryFn: () => navigationService.getMenu(menuId),
    enabled: open,
  })

  const serverItems: StoreNavigationItem[] = menuData?.items ?? []

  // Sincronizar items locales cuando llegan datos del servidor (y no hay orden pendiente)
  useEffect(() => {
    if (serverItems.length > 0 && !isDirtyOrder) {
      setLocalItems([...serverItems].sort((a, b) => a.sort_order - b.sort_order))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuData])



  // ── Drag & Drop ───────────────────────────────────────────────────────────
  const handleMoveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    setLocalItems((prev) => {
      const updated = [...prev]
      const [dragged] = updated.splice(dragIndex, 1)
      updated.splice(hoverIndex, 0, dragged)
      return updated
    })
    setIsDirtyOrder(true)
  }, [])

  // ── Guardar orden ─────────────────────────────────────────────────────────
  const handleSaveOrder = async () => {
    setIsSavingOrder(true)
    try {
      const payload = localItems.map((item, idx) => ({
        id: item.id,
        sort_order: idx + 1,
      }))
      await navigationService.reorderItems(menuId, payload)
      queryClient.invalidateQueries({ queryKey: ['navigation-menu', menuId] })
      setIsDirtyOrder(false)
      toast.success('Orden guardado correctamente')
    } catch {
      toast.error('No se pudo guardar el orden')
    } finally {
      setIsSavingOrder(false)
    }
  }

  // ── Éxito del modal de ítem ───────────────────────────────────────────────
  const handleItemSuccess = () => {
    setItemModalOpen(false)
    setEditingItem(null)
    setIsDirtyOrder(false)
    setLocalItems([]) // forzar re-sincronización desde server
    queryClient.invalidateQueries({ queryKey: ['navigation-menu', menuId] })
  }

  // ── Cambiar estado de ítem ────────────────────────────────────────────────
  const executeToggleStatus = async () => {
    if (!statusItem) return
    try {
      await navigationService.toggleItemStatus(menuId, statusItem.id)
      toast.success('Estado del enlace actualizado')
      setLocalItems([])
      queryClient.invalidateQueries({ queryKey: ['navigation-menu', menuId] })
    } catch {
      toast.error('No se pudo actualizar el estado')
    } finally {
      setStatusItem(null)
    }
  }

  // ── Eliminar ítem ─────────────────────────────────────────────────────────
  const executeDelete = async () => {
    if (!deleteItem) return
    try {
      await navigationService.deleteItem(menuId, deleteItem.id)
      toast.success('Enlace eliminado')
      setLocalItems((prev) => prev.filter((i) => i.id !== deleteItem.id))
      queryClient.invalidateQueries({ queryKey: ['navigation-menu', menuId] })
    } catch {
      toast.error('No se pudo eliminar el enlace')
    } finally {
      setDeleteItem(null)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
              {menuName}
              <span className="text-xs font-normal text-muted-foreground ml-1">
                — Gestión de enlaces
              </span>
            </DialogTitle>
            <DialogDescription className="text-card-description">
              Arrastra los enlaces para reordenarlos. Guarda el orden cuando termines.
            </DialogDescription>
          </DialogHeader>

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 shrink-0 pt-2 pb-3 border-b border-border">
            <Button
              onClick={() => {
                setEditingItem(null)
                setItemModalOpen(true)
              }}
              className="bg-primary hover:bg-primary/90"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Agregar Enlace
            </Button>

            {isDirtyOrder && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-600 font-medium">
                  Hay cambios de orden sin guardar
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setLocalItems([...serverItems].sort((a, b) => a.sort_order - b.sort_order))
                    setIsDirtyOrder(false)
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Descartar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveOrder}
                  disabled={isSavingOrder}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isSavingOrder ? (
                    <Spinner className="h-4 w-4 mr-1" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Guardar orden
                </Button>
              </div>
            )}
          </div>

          {/* Lista */}
          <div className="flex-1 overflow-y-auto py-3">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner className="h-8 w-8" />
              </div>
            ) : (
              <DndProvider backend={HTML5Backend}>
                <NavigationItemsList
                  items={localItems}
                  onMoveItem={handleMoveItem}
                  onEdit={(item) => {
                    setEditingItem(item)
                    setItemModalOpen(true)
                  }}
                  onDelete={(item) => setDeleteItem(item)}
                  onToggleStatus={(item) => setStatusItem(item)}
                />
              </DndProvider>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de ítem */}
      {itemModalOpen && (
        <NavigationItemModal
          open={itemModalOpen}
          onOpenChange={(v) => {
            setItemModalOpen(v)
            if (!v) setEditingItem(null)
          }}
          menuId={menuId}
          item={editingItem}
          onSuccess={handleItemSuccess}
        />
      )}

      {/* Confirmación: cambio de estado */}
      <ConfirmationModal
        isOpen={statusItem !== null}
        onClose={() => setStatusItem(null)}
        onConfirm={executeToggleStatus}
        preset="toggleStatus"
        itemName="enlace"
      />

      {/* Confirmación: eliminar */}
      <ConfirmationModal
        isOpen={deleteItem !== null}
        onClose={() => setDeleteItem(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="enlace"
      />
    </>
  )
}
